import React, { useState, useEffect } from 'react';
import { StopRecord, Meter } from '../../types';
import { XMarkIcon, PencilIcon, CheckIcon, PhotoIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';

interface RecordDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    record: StopRecord | null;
    meter: Meter | null;
    isAdmin: boolean;
    onUpdate: (updatedRecord: StopRecord) => Promise<void>;
}

const formatToInput = (unixTimestamp?: number) => {
    if (!unixTimestamp) return '';
    const d = new Date(unixTimestamp);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const parseFromInput = (value: string) => {
    if (!value) return undefined;
    return new Date(value).getTime();
};

const RecordDetailModal: React.FC<RecordDetailModalProps> = ({
    isOpen, onClose, record, meter, isAdmin, onUpdate
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<Partial<StopRecord>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [enlargedImage, setEnlargedImage] = useState<string | null>(null);

    useEffect(() => {
        if (record) {
            setFormData(record);
            setIsEditing(false);
            setEnlargedImage(null);
        }
    }, [record, isOpen]);

    if (!isOpen || !record) return null;

    const handleSave = async () => {
        setIsSaving(true);
        try {
            if (formData.id) {
                await onUpdate(formData as StopRecord);
            }
            setIsEditing(false);
        } catch (e) {
            console.error(e);
        } finally {
            setIsSaving(false);
        }
    };

    const consumption = (formData.endValue || 0) - (formData.startValue || 0);

    return (
        <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl relative animate-in fade-in zoom-in duration-200 flex flex-col max-h-[90vh]">

                    {/* Header */}
                    <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 bg-gray-50/50 rounded-t-2xl">
                        <div>
                            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                                Detalles del Registro
                            </h2>
                            <p className="text-gray-500 text-xs sm:text-sm mt-1 flex flex-wrap items-center gap-2">
                                <span className="font-mono bg-white px-2 py-0.5 border border-gray-200 rounded">ID: {record.id.split('-')[0]}...</span>
                                <span>{meter?.name} ({meter?.area})</span>
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {isAdmin && !isEditing && (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:text-brand-primary transition-colors font-medium shadow-sm text-sm sm:text-base"
                                >
                                    <PencilIcon className="h-4 w-4" />
                                    <span className="hidden sm:inline">Editar</span>
                                </button>
                            )}
                            {isAdmin && isEditing && (
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-brand-primary text-white rounded-xl hover:bg-brand-secondary transition-colors font-medium shadow-sm disabled:opacity-50 text-sm sm:text-base"
                                >
                                    {isSaving ? (
                                        <div className="h-4 w-4 border-2 border-white border-t-transparent animate-spin rounded-full" />
                                    ) : (
                                        <CheckIcon className="h-5 w-5 stroke-2" />
                                    )}
                                    <span className="hidden sm:inline">Guardar</span>
                                </button>
                            )}
                            <button
                                onClick={onClose}
                                className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-xl transition-colors border border-transparent hover:border-gray-200"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 sm:p-6 overflow-y-auto flex-1 custom-scrollbar">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                            
                            {/* Columna Izquierda: Información de Parada y Mediciones (7 de 12) */}
                            <div className="lg:col-span-7 space-y-6">
                                
                                {/* Main Info */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 bg-amber-50/20 p-4 sm:p-5 rounded-xl border border-amber-100">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Motivo de Parada</label>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={formData.reason || ''}
                                                onChange={e => setFormData({ ...formData, reason: e.target.value })}
                                                className="w-full border-gray-300 rounded-lg p-2.5 shadow-sm focus:border-brand-primary focus:ring-brand-primary"
                                            />
                                        ) : (
                                            <p className="text-base sm:text-lg font-semibold text-gray-900">{formData.reason}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Supervisor a Cargo</label>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={formData.supervisorName || ''}
                                                onChange={e => setFormData({ ...formData, supervisorName: e.target.value })}
                                                className="w-full border-gray-300 rounded-lg p-2.5 shadow-sm focus:border-brand-primary focus:ring-brand-primary"
                                            />
                                        ) : (
                                            <p className="text-base sm:text-lg font-semibold text-gray-900">{formData.supervisorName}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Time & Values Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-4 sm:p-5 rounded-xl border border-gray-100/60 bg-gray-50/50">
                                    {/* INICIO */}
                                    <div className="space-y-4 relative">
                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-300 rounded-full"></div>
                                        <div className="pl-4">
                                            <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Arranque</h3>
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Fecha y Hora</label>
                                                    {isEditing ? (
                                                        <input
                                                            type="datetime-local"
                                                            value={formatToInput(formData.startTime)}
                                                            onChange={e => setFormData({ ...formData, startTime: parseFromInput(e.target.value) })}
                                                            className="w-full border-gray-300 rounded-lg p-2 shadow-sm focus:border-brand-primary focus:ring-brand-primary text-xs"
                                                        />
                                                    ) : (
                                                        <p className="text-gray-900 font-medium text-sm">
                                                            {new Date(formData.startTime || 0).toLocaleString()}
                                                        </p>
                                                    )}
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Lectura Inicial ({meter?.unit})</label>
                                                    {isEditing ? (
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={formData.startValue || ''}
                                                            onChange={e => setFormData({ ...formData, startValue: parseFloat(e.target.value) })}
                                                            className="w-full border-gray-300 rounded-lg p-2 shadow-sm focus:border-brand-primary focus:ring-brand-primary font-mono text-xs"
                                                        />
                                                    ) : (
                                                        <p className="text-gray-900 font-mono font-bold text-base">
                                                            {formData.startValue?.toLocaleString()}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* FIN */}
                                    <div className="space-y-4 relative">
                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#E2B53F]/40 rounded-full"></div>
                                        <div className="pl-4">
                                            <h3 className="text-xs font-black text-[#E2B53F] uppercase tracking-widest mb-3">Finalización</h3>
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Fecha y Hora</label>
                                                    {isEditing ? (
                                                        <input
                                                            type="datetime-local"
                                                            value={formatToInput(formData.endTime)}
                                                            onChange={e => setFormData({ ...formData, endTime: parseFromInput(e.target.value) })}
                                                            className="w-full border-gray-300 rounded-lg p-2 shadow-sm focus:border-brand-primary focus:ring-brand-primary text-xs"
                                                        />
                                                    ) : (
                                                        <p className="text-gray-900 font-medium text-sm">
                                                            {formData.endTime ? new Date(formData.endTime).toLocaleString() : 'No finalizado'}
                                                        </p>
                                                    )}
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Lectura Final ({meter?.unit})</label>
                                                    {isEditing ? (
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={formData.endValue || ''}
                                                            onChange={e => setFormData({ ...formData, endValue: parseFloat(e.target.value) })}
                                                            className="w-full border-gray-300 rounded-lg p-2 shadow-sm focus:border-brand-primary focus:ring-brand-primary font-mono text-xs"
                                                        />
                                                    ) : (
                                                        <p className="text-gray-900 font-mono font-bold text-base">
                                                            {formData.endValue?.toLocaleString() || '---'}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Consumo Calculado Summary - MEJORADO CON CONTRASTE AMARILLO/NEGRO */}
                                <div className="bg-brand-primary text-gray-900 rounded-xl p-4 sm:p-5 shadow-md flex flex-row justify-between items-center font-semibold">
                                    <div>
                                        <p className="text-gray-900/80 text-xs font-bold uppercase tracking-wider">Consumo Total Calculado</p>
                                    </div>
                                    <div className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">
                                        {consumption > 0 ? consumption.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                                        <span className="text-sm font-medium text-gray-900/80 ml-1.5">{meter?.unit}</span>
                                    </div>
                                </div>

                            </div>

                            {/* Columna Derecha: Evidencia Fotográfica (5 de 12) */}
                            <div className="lg:col-span-5 flex flex-col space-y-4">
                                <h3 className="text-xs sm:text-sm font-black text-gray-800 uppercase tracking-widest flex items-center border-b border-gray-100 pb-2">
                                    <PhotoIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-gray-400" />
                                    Evidencia Fotográfica
                                </h3>

                                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
                                    {/* Foto Arranque */}
                                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex flex-col justify-between">
                                        <p className="text-xs font-bold text-gray-500 uppercase mb-2 text-center">Foto Arranque</p>
                                        {formData.startPhoto ? (
                                            <div className="relative group cursor-pointer" onClick={() => setEnlargedImage(formData.startPhoto as string)}>
                                                <img src={formData.startPhoto} alt="Arranque" className="w-full h-32 sm:h-40 object-cover rounded-lg shadow-sm transition-transform group-hover:opacity-90" />
                                                <div className="absolute inset-0 flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity bg-black/20 rounded-lg backdrop-blur-[1px]">
                                                    <div className="bg-white/90 p-1.5 rounded-full shadow-lg">
                                                        <ArrowsPointingOutIcon className="h-4 w-4 text-gray-900" />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="w-full h-32 sm:h-40 bg-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-xs italic">Sin evidencia</div>
                                        )}
                                    </div>

                                    {/* Foto Cierre */}
                                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex flex-col justify-between">
                                        <p className="text-xs font-bold text-gray-500 uppercase mb-2 text-center">Foto Cierre</p>
                                        {formData.endPhoto ? (
                                            <div className="relative group cursor-pointer" onClick={() => setEnlargedImage(formData.endPhoto as string)}>
                                                <img src={formData.endPhoto} alt="Cierre" className="w-full h-32 sm:h-40 object-cover rounded-lg shadow-sm transition-transform group-hover:opacity-90" />
                                                <div className="absolute inset-0 flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity bg-black/20 rounded-lg backdrop-blur-[1px]">
                                                    <div className="bg-white/90 p-1.5 rounded-full shadow-lg">
                                                        <ArrowsPointingOutIcon className="h-4 w-4 text-gray-900" />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="w-full h-32 sm:h-40 bg-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-xs italic">Sin evidencia</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* Lightbox / Image Zoom Overlay */}
            {enlargedImage && (
                <div
                    className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-200"
                    onClick={() => setEnlargedImage(null)}
                >
                    <button
                        className="absolute flex items-center justify-center top-4 right-4 sm:top-6 sm:right-6 text-white bg-white/10 hover:bg-white/20 p-2 sm:p-3 rounded-full backdrop-blur-sm transition-colors z-10"
                        onClick={(e) => {
                            e.stopPropagation();
                            setEnlargedImage(null);
                        }}
                    >
                        <XMarkIcon className="h-6 w-6 sm:h-8 sm:w-8" />
                    </button>
                    <img
                        src={enlargedImage}
                        alt="Evidencia ampliada"
                        className="max-w-full max-h-full object-contain rounded-xl shadow-2xl animate-in zoom-in-95 duration-300"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </>
    );
};

export default RecordDetailModal;
